import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, ArrowRight, Gift, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { business, addressLine1, addressLine2 } from "@/lib/business-data";
import BrandLogo from "@/components/BrandLogo";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Locations", to: "/locations" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
];

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    /* Solid deep-navy surface — deliberately not a gradient. */
    <footer className="section-dark text-white on-dark">
      {/* CTA banner */}
      <div className="relative overflow-hidden" style={{ background: "hsl(var(--primary))" }}>
        <div aria-hidden="true" className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="container-max px-4 py-12 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-white font-bold tracking-tight">
                Got a repair list? Let's get it done.
              </h2>
              <p className="text-white/85 text-sm md:text-base mt-1.5">
                Free written estimates across Hayward and the East Bay — {business.hours.label.toLowerCase()}.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Button
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
              >
                Get Free Estimate <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="gap-2 rounded-xl font-bold border-2 border-white/60 bg-transparent text-white hover:bg-white/15 hover:text-white hover:border-white"
              >
                <a href={business.phoneHref}>
                  <Phone className="h-4 w-4" /> {business.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div>
            <div className="mb-5 inline-flex items-center justify-center rounded-2xl bg-white p-4 shadow-premium">
              <BrandLogo height={64} className="h-16 w-auto" loading="lazy" />
            </div>
            <p className="text-[hsl(220_25%_88%)] text-sm leading-relaxed">
              Local handyman, repair, and remodeling services for homes across Hayward and the wider East
              Bay. Owned and run by {business.owner}. Se habla español.
            </p>
            <div className="mt-5 rounded-xl border border-white/15 bg-white/[0.07] px-4 py-3">
              <p className="flex items-start gap-2 text-sm font-semibold text-white">
                <Gift className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--secondary-on-dark))" }} />
                <span>{business.offers.headline}</span>
              </p>
            </div>
            <a
              href={business.yelpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[hsl(220_25%_88%)] hover:text-white transition-colors"
            >
              <Star className="h-4 w-4" style={{ color: "hsl(var(--secondary-on-dark))" }} />
              Find us on Yelp
            </a>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">Services</h3>
            <ul className="space-y-2">
              {services.slice(0, 8).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-[hsl(220_25%_86%)] hover:text-white text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-1 text-sm font-bold transition-colors"
                  style={{ color: "hsl(var(--primary-on-dark))" }}
                >
                  View all services <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Service areas */}
          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">Service Areas</h3>
            <ul className="space-y-2">
              {locations.slice(0, 8).map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/locations/${l.slug}`}
                    className="text-[hsl(220_25%_86%)] hover:text-white text-sm transition-colors"
                  >
                    {l.name}, {l.state}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/locations"
                  className="inline-flex items-center gap-1 text-sm font-bold transition-colors"
                  style={{ color: "hsl(var(--primary-on-dark))" }}
                >
                  View all locations <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">Contact</h3>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--primary-on-dark))" }} />
                <a href={business.phoneHref} className="font-bold text-white hover:opacity-80 transition-opacity">
                  {business.phone}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--primary-on-dark))" }} />
                <a
                  href={business.emailHref}
                  className="text-[hsl(220_25%_88%)] hover:text-white transition-colors break-all"
                >
                  {business.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--primary-on-dark))" }} />
                <span className="text-[hsl(220_25%_88%)]">
                  {addressLine1}
                  <br />
                  {addressLine2}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--primary-on-dark))" }} />
                <span className="text-[hsl(220_25%_88%)]">
                  {business.hours.label}
                  <br />
                  {business.hours.time}
                  <br />
                  <span className="text-[hsl(220_20%_74%)] text-xs">{business.hours.closed}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/15 flex flex-col md:flex-row items-center justify-between gap-5">
          <p className="text-[hsl(220_20%_76%)] text-xs text-center md:text-left">
            © {new Date().getFullYear()} {business.name}. All rights reserved.
          </p>
          <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {quickLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-[hsl(220_25%_86%)] hover:text-white text-xs font-medium transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="text-[hsl(220_20%_76%)] text-xs">{business.tagline}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
